// The breadcrumb trail registry — the ONE place a page's trail is defined
// (docs/design-system/breadcrumb-trail.md §9). `cos-app-shell` renders
// `cos-trail` from what `resolveTrail()` returns here; pages never render
// breadcrumbs or Back links of their own.
//
// Every pattern in routes.ts's ROUTE_PATTERNS has exactly one entry, and
// lint/trail-registry-drift.test.ts fails the build if that stops being
// true, if a `section` stops being a real rail (or header-chrome) href, or
// if an `alternateParents` pattern is not a registered route. It is the
// same mirror-plus-lint arrangement routes.ts itself uses.
//
// Deliberately NOT in here: the section crumb's LABEL and ICON. Those are
// read from the live rail at render time (the shell passes its `_navItems()`
// plus the full rail sets into `resolveTrail`), so a tenant that renames a
// module, or a workspace that labels one differently, gets the trail its own
// rail shows. Copying "Digital Archives" into this file would be a second
// source of truth for the same string.

import { ROUTE_PATTERNS, matchingRoutePattern, routePatternMatches } from '../routes.js';

/** The registry's own fallback for a module segment: a number is never a
 *  module name, so it reads "Module" until the page says otherwise. */
function moduleSegmentFallback(segment: string): string {
	return /^\d+$/.test(segment) ? 'Module' : humaniseSegment(segment);
}

/** One rendered step of the trail. `href` absent means "not a link" (the
 *  current page, or an intermediate whose page does not exist as a route). */
export interface Crumb {
	label: string;
	href?: string;
	icon?: string;
	current?: boolean;
}

/** A navigable section as the RAIL knows it — href, rendered label, glyph.
 *  The shell supplies these; the registry only ever stores the href. */
export interface TrailSection {
	href: string;
	label: string;
	icon: string;
}

/** What a registry entry's `crumbs`/`leafFallback` callbacks get to work
 *  with: the matched route's params, the live path and query, and the
 *  trailing wildcard segments for a `*` pattern (`/thrive/*`). */
export interface TrailParams {
	params: Record<string, string>;
	path: string;
	query: URLSearchParams;
	tail: string[];
}

/** How the CURRENT page's own crumb is produced.
 *
 *  - `static`  — the label is known from the route (and its params/query):
 *                `leafFallback` IS the label, rendered immediately.
 *  - `record`  — only the page knows it, so it calls `services/trail.ts`'s
 *                `setLeaf()`; the shell shows a skeleton until it arrives
 *                and `leafFallback` after 3s.
 *  - `none`    — a top-level rail landing: the section crumb IS the current
 *                crumb (spec §4.4), and a leaf is IGNORED unless the entry
 *                declares `acceptsLeaf`. Spec §4.8 names exactly three pages
 *                that acquire one that way — Mail, Conversations and Desk,
 *                which have no title of their own until a thread or a service
 *                point is chosen — and only those three declare it. Every
 *                other rail landing refuses, which is what stops a leaf that
 *                outlived its own page from decorating whatever the user
 *                opened next (see `acceptsLeaf` below).
 *
 *  `none` is an addition to the spec's `'static' | 'record'` pair: without
 *  it a top-level page would have to be detected by comparing its path to
 *  its section href, which is wrong for `/outreach/visits` (the rail item
 *  labelled "Outreach", whose landing page is "Visits"). */
export type TrailLeafKind = 'static' | 'record' | 'none';

export interface TrailEntry {
	/** One of ROUTE_PATTERNS. */
	pattern: string;
	/** The rail item href this page belongs to, or null for a page with no
	 *  shell at all (`/login`, the carousel) and for redirect-only routes
	 *  that navigate away before anything paints. Null means "render no
	 *  trail". */
	section: string | null;
	/** Crumbs BETWEEN the section and the current page (spec §4.2 — the URL
	 *  hierarchy, not the file tree). */
	crumbs?: (p: TrailParams) => Crumb[];
	leaf: TrailLeafKind;
	/** `none` entries only: this page supplies its own leaf (spec §4.8), so a
	 *  published one is appended to the section crumb.
	 *
	 *  It is opt-in because the alternative leaks. A page's last render can
	 *  land AFTER the route changed — Lit flushes an update queued before the
	 *  element disconnected, and by then `location.pathname` is already the
	 *  new page, so nothing about the call says it is stale. When every rail
	 *  landing accepted any leaf on offer, one such straggler turned
	 *  /action-center into "Action Center > College Park Airport terminal
	 *  building" and it stayed there. A landing that never takes a leaf
	 *  cannot be decorated by one. */
	acceptsLeaf?: boolean;
	/** The current crumb's label for `static`, or the after-3s label for
	 *  `record`. A function so a route whose identity IS in the URL (a
	 *  ticket's COM- number, a Preserve entity's tag text, a Measure drill
	 *  level) can say so; the spec types this `string`, and a plain string
	 *  still works. An empty result means "no current crumb". */
	leafFallback: string | ((p: TrailParams) => string);
	/** Routes that are legitimate ways INTO this page besides its canonical
	 *  parent (spec §5). If `history.state.from` matches one, it replaces the
	 *  parent crumb. */
	alternateParents?: string[];
}

// Header-chrome destinations. These three are reached from the header and
// the account menu, never from the rail, so there is no rail item to read a
// label off — they are their own section. Everything else resolves against
// the live rail.
export const CHROME_SECTIONS: readonly TrailSection[] = [
	{ href: '/notifications', label: 'Notifications', icon: 'bell' },
	{ href: '/settings', label: 'Settings', icon: 'settings' },
	// The account menu's "Activity is logged" link. 'chart' because the icon
	// set has no activity glyph and the page is a numbers comparison.
	{ href: '/activity', label: 'My activity', icon: 'chart' },
];

// ── entry helpers ────────────────────────────────────────────────────────

/** A page with no shell, or a route that redirects before it paints. */
const bare = (pattern: string): TrailEntry => ({ pattern, section: null, leaf: 'none', leafFallback: '' });

/** A top-level rail landing: the section crumb alone (spec §4.4). */
const top = (pattern: string, section: string): TrailEntry => ({ pattern, section, leaf: 'none', leafFallback: '' });

/** Explore's tab labels. The trail must not render a slug, and these are
 *  the same words the page's own tab strip uses. */
const EXPLORE_TAB_LABELS: Record<string, string> = {
	curation: 'Curation',
	settings: 'Search settings',
	imports: 'Imports',
};

/** A rail landing with no title of its own, which names itself once the user
 *  picks something on it: a mail thread, a conversation, a desk service point
 *  (spec §4.8). The ONLY kind of `none` entry that takes a page's leaf. */
const topNamedByPage = (pattern: string, section: string): TrailEntry => ({
	pattern,
	section,
	leaf: 'none',
	acceptsLeaf: true,
	leafFallback: '',
});

/** A page whose own label the route already knows. */
const fixed = (
	pattern: string,
	section: string,
	label: string | ((p: TrailParams) => string),
	crumbs?: (p: TrailParams) => Crumb[],
): TrailEntry => ({ pattern, section, leaf: 'static', leafFallback: label, crumbs });

/** A page whose label only arrives with the record (`trail.setLeaf`). */
const record = (
	pattern: string,
	section: string,
	fallback: string | ((p: TrailParams) => string),
	opts: { crumbs?: (p: TrailParams) => Crumb[]; alternateParents?: string[] } = {},
): TrailEntry => ({
	pattern,
	section,
	leaf: 'record',
	leafFallback: fallback,
	crumbs: opts.crumbs,
	alternateParents: opts.alternateParents,
});

/** A fixed intermediate crumb. */
const step = (label: string, href?: string): Crumb => ({ label, href });

/** An intermediate crumb standing in for a RECORD: the contract between
 *  Contracts and Amend, the partner between Partners and Peer comparison.
 *  The id from the URL is only the FALLBACK — the page publishes the real
 *  name with `services/trail.ts`'s `setCrumbLabel(href, name)`, keyed by this
 *  crumb's own href, and resolveTrail swaps it in. Spec §4.3 says a trail
 *  never shows a bare id, so any route using this must have a page that
 *  names it. */
const recordStep = (value: string, href: string): Crumb => ({ label: value, href });

// Circulate's staff modules (components/cos-circulate-page.ts's MODULES +
// LEND_MODULE), keyed by the `?m=` value. Bare `/circulate` is the rail
// landing and shows the section alone.
const CIRCULATE_MODULES: Record<string, string> = {
	circulation: 'Circulation',
	cataloging: 'Cataloging',
	acquisitions: 'Acquisitions',
	ill: 'ILL',
	serials: 'Serials',
	collection: 'Collection',
	notices: 'Notices',
	authority: 'Authority',
	reporting: 'Reporting',
	permissions: 'Permissions',
	migration: 'Migration',
	lend: 'Lend',
};

/** "circ-physical" -> "Circ physical". Used for the Measure drill, whose
 *  level labels are registered at runtime (measure/registry.ts) and so
 *  cannot be known statically; the drill page itself calls `setLeaf` with
 *  the real one. */
export function humaniseSegment(segment: string): string {
	const words = decodeURIComponent(segment).replace(/[-_]+/g, ' ').trim();
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
}

const thriveCrumbs = (p: TrailParams): Crumb[] =>
	p.tail.slice(0, -1).map((seg, i) => step(humaniseSegment(seg), `/thrive/${p.tail.slice(0, i + 1).join('/')}`));

// ── the registry ─────────────────────────────────────────────────────────

import { SUPPORT_EMAIL_PAGE_ENABLED } from '../utils/supportEmailSwitch.js';

export const TRAIL_REGISTRY: TrailEntry[] = [
	// Bare or redirect-only: no shell, or gone before it paints.
	bare('/'),
	bare('/login'),
	bare('/panels'),
	bare('/clock/:token'),
	bare('/reports/download/:token'),
	bare('/s/:token'),
	bare('/broadcast/carousel/:client/:configId'),
	bare('/chat'),
	bare('/bookmobile'),
	bare('/bookmobile/stops'),
	bare('/bookmobile/routes'),
	bare('/outreach'),
	bare('/outreach/bookmobile/vehicles'),
	bare('/implementation/new'),
	// DEV-only harness, not reachable from any rail (app.ts gates it on
	// import.meta.env.DEV), so it has no section to root a trail at.
	bare('/dev/record-shell'),

	// Header chrome.
	top('/notifications', '/notifications'),
	top('/settings', '/settings'),
	top('/activity', '/activity'),

	// Communico workspace — the personal surfaces.
	top('/inbox', '/inbox'),
	top('/dashboard', '/dashboard'),
	top('/me/implementations', '/me/implementations'),
	top('/tasks', '/tasks'),
	top('/timeoff', '/timeoff'),
	top('/training', '/training'),
	record('/training/:key', '/training', (p) => moduleSegmentFallback(p.params.key)),
	fixed('/training/:key/attempt/:id', '/training', 'Test attempt', (p) => [
		step(moduleSegmentFallback(p.params.key), `/training/${p.params.key}`),
	]),
	fixed('/training/:key/results/:id', '/training', 'Results', (p) => [
		step(moduleSegmentFallback(p.params.key), `/training/${p.params.key}`),
	]),
	top('/calendar', '/calendar'),
	fixed('/calendar/team', '/calendar', 'Team'),
	// Mail, Conversations and Desk have no title of their own until a thread
	// or a service point is chosen (spec §4.8): section crumb, plus whatever
	// the page later sets as its leaf. The only three entries that take one.
	topNamedByPage('/mail', '/mail'),
	topNamedByPage('/conversations', '/conversations'),
	topNamedByPage('/desk', '/desk'),
	top('/files', '/files'),

	// Reports & dashboards.
	top('/reports', '/reports'),
	// §12.2: a saved dashboard sits under Reports, the rail entry it is
	// listed under, rather than under Dashboard.
	record('/dashboards/:id', '/reports', 'Dashboard'),
	// The activity-report drill-through target (app.ts's own note).
	fixed('/activities', '/reports', 'Activities'),
	top('/report-builder', '/report-builder'),
	fixed('/report-builder/new', '/report-builder', 'New report'),
	record('/report-builder/:id', '/report-builder', 'Report'),

	// Tickets.
	top('/tickets', '/tickets'),
	fixed('/tickets/new', '/tickets', 'New ticket'),
	fixed('/tickets/mine', '/tickets', 'My tickets'),
	...(SUPPORT_EMAIL_PAGE_ENABLED ? [fixed('/tickets/support-email', '/tickets', 'Support email')] : []),
	// The address carries only the row id, which is not what anyone calls a
	// ticket, so the default crumb is neutral; the page sets the real one (the
	// server's reference) with setLeaf once the ticket loads.
	record('/tickets/:id', '/tickets', 'Ticket', {
		alternateParents: ['/tickets/mine', '/partners/:id'],
	}),

	// Projects & implementation.
	top('/projects', '/projects'),
	fixed('/projects/new', '/projects', 'New project'),
	record('/projects/:id', '/projects', 'Project'),
	top('/implementation', '/implementation'),
	record('/implementation/:moduleName', '/implementation', (p) => moduleSegmentFallback(p.params.moduleName)),

	// Partners, prospects, contacts, deals.
	top('/partners', '/partners'),
	fixed('/partners/map', '/partners', 'Map'),
	record('/partners/:id', '/partners', 'Partner', {
		alternateParents: ['/partners/map', '/prospects/by-partner/:id'],
	}),
	fixed('/partners/:id/implementation', '/partners', 'Implementation', (p) => [
		recordStep(p.params.id, `/partners/${p.params.id}`),
	]),
	// A record leaf, like its /implementation/:moduleName twin: the URL's
	// module segment is sometimes a module id rather than a name, so the page
	// supplies the display name and the humanised segment is only the fallback.
	record('/partners/:id/implementation/:moduleName', '/partners', (p) => moduleSegmentFallback(p.params.moduleName), {
		crumbs: (p) => [
			recordStep(p.params.id, `/partners/${p.params.id}`),
			step('Implementation', `/partners/${p.params.id}/implementation`),
		],
	}),
	top('/prospects', '/prospects'),
	record('/prospects/by-partner/:id', '/prospects', 'Prospect'),
	record('/prospects/:fscskey', '/prospects', 'Prospect'),
	// Peer comparison for a PARTNER roots at Partners, not Prospects: the
	// subject is a partner we already have (the fscskey twin below is the
	// Prospects one).
	fixed('/peer-comparison/partner/:id', '/partners', 'Peer comparison', (p) => [
		recordStep(p.params.id, `/partners/${p.params.id}`),
	]),
	fixed('/peer-comparison/fscskey/:fscskey', '/prospects', 'Peer comparison', (p) => [
		recordStep(p.params.fscskey, `/prospects/${p.params.fscskey}`),
	]),
	top('/contacts', '/contacts'),
	record('/contacts/:id', '/contacts', 'Contact', { alternateParents: ['/partners/:id'] }),
	top('/deals', '/deals'),
	fixed('/deals/board', '/deals', 'Board'),
	record('/deals/:id', '/deals', 'Deal', { alternateParents: ['/deals/board'] }),

	// Billing. Contracts, Customers, Invoices and the rest are each their OWN
	// rail row, so each is its own section: on /billing/contracts/2202 the
	// rail lights "Contracts", and the trail must start there too. Only
	// /billing/close and /billing/reminder-settings light no row at all, and
	// they fall back to the cluster home.
	top('/billing', '/billing'),
	top('/billing/renewals', '/billing/renewals'),
	top('/billing/contracts', '/billing/contracts'),
	fixed('/billing/contracts/new', '/billing/contracts', 'New contract'),
	record('/billing/contracts/:id', '/billing/contracts', 'Contract'),
	fixed('/billing/contracts/:id/renew', '/billing/contracts', 'Renew', (p) => [
		recordStep(p.params.id, `/billing/contracts/${p.params.id}`),
	]),
	fixed('/billing/contracts/:id/amend', '/billing/contracts', 'Amend', (p) => [
		recordStep(p.params.id, `/billing/contracts/${p.params.id}`),
	]),
	top('/billing/accounts', '/billing/accounts'),
	record('/billing/accounts/:id', '/billing/accounts', 'Billing account'),
	top('/billing/customers', '/billing/customers'),
	record('/billing/customers/:id', '/billing/customers', 'Customer'),
	top('/billing/products', '/billing/products'),
	top('/billing/documents', '/billing/documents'),
	top('/billing/invoices', '/billing/invoices'),
	record('/billing/invoices/:id', '/billing/invoices', 'Invoice'),
	fixed('/billing/close', '/billing', 'Month-end close'),
	top('/billing/reports', '/billing/reports'),
	// Cheque intake. A top entry rather than a record one: the open
	// batch lives in ?batch= rather than in the path, so there is one
	// route and the page's own header names the batch.
	top('/billing/deposits', '/billing/deposits'),
	// Bank activity. A top entry for the same reason cheque intake is
	// one: the open line lives in the query rather than in the path, so
	// there is a single route and the panel's own heading names the line.
	top('/billing/bank-activity', '/billing/bank-activity'),
	top('/billing/reconciliation', '/billing/reconciliation'),
	top('/billing/invoice-settings', '/billing/invoice-settings'),
	fixed('/billing/reminder-settings', '/billing', 'Reminders', () => [
		step('Invoice settings', '/billing/invoice-settings'),
	]),

	// Knowledge base.
	top('/kb', '/kb'),
	fixed('/kb/new', '/kb', 'New article'),
	record('/kb/:slug', '/kb', 'Article'),
	fixed('/kb/:slug/edit', '/kb', 'Edit', (p) => [recordStep(humaniseSegment(p.params.slug), `/kb/${p.params.slug}`)]),
	fixed('/kb/:slug/versions', '/kb', 'Version history', (p) => [
		recordStep(humaniseSegment(p.params.slug), `/kb/${p.params.slug}`),
	]),

	// Community, comms, feedback.
	top('/community', '/community'),
	top('/communications', '/communications'),
	top('/feedback', '/feedback'),

	// Partner workspace — products.
	// `/circulate` bare is the rail landing; `?m=` names the module tab.
	fixed('/circulate', '/circulate', (p) => CIRCULATE_MODULES[p.query.get('m') ?? ''] ?? ''),
	top('/lend', '/lend'),
	top('/media', '/media'),
	top('/collections', '/collections'),
	// Explore admin. The landing IS the section crumb, and each tab is a
	// crumb under it: the tab lives in the path (/explore/curation), not
	// a query param, so a staffer can link a colleague straight to the
	// screen they are looking at. An unknown tab segment humanises rather
	// than 404s, which is what /explore/:tab's own guard relies on.
	top('/annual-report', '/annual-report'),
	top('/explore', '/explore'),
	fixed('/explore/:tab', '/explore', (p) => EXPLORE_TAB_LABELS[p.params.tab] ?? humaniseSegment(p.params.tab)),
	top('/broadcast', '/broadcast'),
	top('/partner-admin', '/partner-admin'),
	top('/action-center', '/action-center'),
	top('/roster', '/roster'),

	top('/reserve', '/reserve'),
	fixed('/reserve/bookings', '/reserve', 'Bookings'),
	fixed('/reserve/calendar', '/reserve', 'Calendar'),
	fixed('/reserve/rooms', '/reserve', 'Rooms'),
	top('/schedule', '/schedule'),
	fixed('/schedule/new', '/schedule', 'New appointment'),
	record('/schedule/bookings/:id', '/schedule', 'Appointment'),
	record('/schedule/bookings/:id/edit', '/schedule', 'Edit appointment'),
	// Stage 4, slice A (categories).
	fixed('/schedule/settings', '/schedule', 'Settings'),
	fixed('/schedule/settings/categories/new', '/schedule', 'New category', () => [step('Settings', '/schedule/settings')]),
	record('/schedule/settings/categories/:id', '/schedule', 'Category', { crumbs: () => [step('Settings', '/schedule/settings')] }),
	// Stage 4, slice C (providers).
	record('/schedule/settings/categories/:id/providers', '/schedule', 'Providers'),
	record('/schedule/settings/providers/:id/week', '/schedule', 'Provider week'),
	fixed('/schedule/settings/groups/new', '/schedule', 'New group', () => [step('Settings', '/schedule/settings')]),
	record('/schedule/settings/groups/:id', '/schedule', 'Group', { crumbs: () => [step('Settings', '/schedule/settings')] }),

	top('/attend', '/attend'),
	fixed('/attend/events/new', '/attend', 'New event'),
	record('/attend/events/:id/edit', '/attend', 'Event'),

	top('/preserve', '/preserve'),
	fixed('/preserve/items/new', '/preserve', 'New item'),
	record('/preserve/items/:id', '/preserve', 'Item', {
		alternateParents: ['/preserve/unlinked', '/preserve/entities/:tagType/:tagText'],
	}),
	fixed('/preserve/items/:id/recording', '/preserve', 'Recording review', (p) => [
		{ label: 'Item', href: `/preserve/items/${p.params.id}` },
	]),
	fixed('/preserve/oral-history/:id', '/preserve', 'Oral history tag review'),
	record('/preserve/contributions/:id', '/preserve', 'Contribution'),
	// The entity hub's identity is its own tag text, right there in the URL.
	fixed('/preserve/entities/:tagType/:tagText', '/preserve', (p) => decodeURIComponent(p.params.tagText)),
	fixed('/preserve/unlinked', '/preserve', 'Unlinked items'),

	// Outreach — ONE rail entry ("Outreach", href /outreach/visits) covering
	// both sections. Its landing IS the rail row, so it is a top-level page;
	// a "Visits" crumb under it would just repeat the section.
	top('/outreach/visits', '/outreach/visits'),
	record('/outreach/visits/:id', '/outreach/visits', 'Visit'),
	fixed('/outreach/bookmobile', '/outreach/visits', 'Bookmobile'),
	fixed('/outreach/bookmobile/stops', '/outreach/visits', 'Stops', () => [
		step('Bookmobile', '/outreach/bookmobile'),
	]),
	fixed('/outreach/bookmobile/routes', '/outreach/visits', 'Routes', () => [
		step('Bookmobile', '/outreach/bookmobile'),
	]),
	fixed('/outreach/bookmobile/trips', '/outreach/visits', 'Trips', () => [
		step('Bookmobile', '/outreach/bookmobile'),
	]),
	fixed('/outreach/bookmobile/public-timetable', '/outreach/visits', 'Public timetable', () => [
		step('Bookmobile', '/outreach/bookmobile'),
	]),

	// Measure retired into Thrive (Paul, 2026-09-06) - all three routes
	// redirect before anything paints, same as the /bookmobile* entries above.
	bare('/measure'),
	bare('/measure/pipeline'),
	bare('/measure/d'),
	bare('/measure/d/*'),

	// Thrive.
	top('/thrive', '/thrive'),
	// Thrive's page labels are registry-driven (thrive/registry.ts) - the
	// page supplies the real leaf; the tail gives an honest fallback + crumbs
	// in the meantime, the /measure/d/* precedent.
	record('/thrive/*', '/thrive', (p) => humaniseSegment(p.tail.at(-1) ?? '') || 'Detail', {
		crumbs: thriveCrumbs,
	}),

	// Nurture is five rail rows over one URL tree — Audience (/nurture),
	// Marketing (/nurture/segments, which also owns emails, campaigns and
	// journeys), Surveys, Shared email and the Boardroom — and each one is its own
	// section, because each is the row the rail lights.
	top('/nurture', '/nurture'),
	// Audiences is the Marketing row's landing tab, so it reads
	// "Marketing > Audiences" like its sibling tabs rather than stopping at "Marketing".
	fixed('/nurture/segments', '/nurture/segments', 'Audiences'),
	fixed('/nurture/emails', '/nurture/segments', 'Emails'),
	fixed('/nurture/emails/new/studio', '/nurture/segments', 'New email', () => [step('Emails', '/nurture/emails')]),
	record('/nurture/emails/:id/studio', '/nurture/segments', 'Email', {
		crumbs: () => [step('Emails', '/nurture/emails')],
		// The email studio's old `?back=` parameter, as an arrival rule.
		alternateParents: ['/nurture/journeys', '/nurture/campaigns'],
	}),
	fixed('/nurture/campaigns', '/nurture/segments', 'Campaigns'),
	record('/nurture/campaigns/:id/studio', '/nurture/segments', 'Campaign', {
		crumbs: () => [step('Campaigns', '/nurture/campaigns')],
		alternateParents: ['/nurture/journeys'],
	}),
	fixed('/nurture/journeys', '/nurture/segments', 'Journeys'),
	top('/nurture/surveys', '/nurture/surveys'),
	fixed('/nurture/surveys/new', '/nurture/surveys', 'New survey'),
	record('/nurture/surveys/:sid', '/nurture/surveys', 'Survey'),
	fixed('/nurture/orgs', '/nurture', 'Organisations'),
	fixed('/nurture/work-queue', '/nurture', 'Work queue'),
	top('/nurture/inbound-email', '/nurture/inbound-email'),
	top('/nurture/board', '/nurture/board'),
	// Registered LAST of the /nurture routes in app.ts so the literals above
	// are never parsed as an id; matchingRoutePattern honours that order.
	// A record opened from the Contacts list keeps the list's own address
	// (search, stage chip, type tab) so the first crumb returns to it.
	record('/nurture/:id', '/nurture', 'Person', { alternateParents: ['/nurture'] }),

	// Admin.
	top('/admin', '/admin'),
	fixed('/admin/users', '/admin', 'Users'),
	fixed('/admin/audit', '/admin', 'Audit log'),
	fixed('/admin/feedback', '/admin', 'Feedback'),
	fixed('/admin/activity', '/admin', 'Activity'),
	fixed('/admin/timeoff', '/admin', 'Time off'),
	fixed('/admin/training', '/admin', 'Training'),
	fixed('/admin/labels', '/admin', 'Labels'),
	fixed('/admin/chat-channels', '/admin', 'Chat channels'),
	fixed('/admin/module-templates', '/admin', 'Module templates'),
	fixed('/admin/kb-article-types', '/admin', 'KB article types'),
	fixed('/admin/partner-ils-types', '/admin', 'Partner ILS types'),
	fixed('/admin/partner-discovery-layers', '/admin', 'Partner discovery layers'),
	fixed('/admin/partner-purchase-types', '/admin', 'Partner purchase types'),
	fixed('/admin/partner-statuses', '/admin', 'Partner statuses'),
	fixed('/admin/partner-zoom-config-types', '/admin', 'Partner Zoom config types'),
	fixed('/admin/partner-currencies', '/admin', 'Partner currencies'),
	fixed('/admin/partner-consortiums', '/admin', 'Consortiums & buying groups'),
	fixed('/admin/partner-module-statuses', '/admin', 'Module statuses'),
	fixed('/admin/project-phases', '/admin', 'Project phases'),
	fixed('/admin/project-templates', '/admin', 'Project templates'),
	fixed('/admin/escalation-rules', '/admin', 'Escalation rules'),
	fixed('/admin/derivation-rules', '/admin', 'Conversation rules'),
	fixed('/admin/ticket-templates', '/admin', 'Ticket templates'),
	fixed('/admin/custom-fields', '/admin', 'Custom fields'),
	fixed('/admin/preserve-limits', '/admin', 'Preserve limits'),
	fixed('/admin/preserve-ia-import', '/admin', 'Internet Archive import'),
	fixed('/admin/client-settings', '/admin', 'Client settings'),
	fixed('/admin/schema-explorer', '/admin', 'Schema explorer'),
	fixed('/admin/data-console', '/admin', 'Data console'),
	fixed('/admin/mcp-library-tools', '/admin', 'Library tools'),
	// /admin/pls has no route of its own, so the group crumb is plain text.
	fixed('/admin/pls/upload', '/admin', 'Upload', () => [step('IMLS PLS data')]),
	fixed('/admin/pls/link-suggestions', '/admin', 'Link suggestions', () => [step('IMLS PLS data')]),
	fixed('/admin/integrations', '/admin', 'Integrations'),
	fixed('/admin/agents', '/admin', 'Agents'),
	fixed('/admin/agents/new', '/admin', 'New agent', () => [step('Agents', '/admin/agents')]),
	record('/admin/agents/:id', '/admin', 'Agent', { crumbs: () => [step('Agents', '/admin/agents')] }),
	fixed('/admin/agent-playbooks', '/admin', 'Playbooks'),
	fixed('/admin/decision-engines', '/admin', 'Decision models'),
	fixed('/admin/workflows', '/admin', 'Workflows'),
	record('/admin/workflows/:id', '/admin', 'Workflow', { crumbs: () => [step('Workflows', '/admin/workflows')] }),
	fixed('/admin/github', '/admin', 'GitHub'),
	fixed('/settings/github', '/settings', 'Connect GitHub'),
	fixed('/admin/agent-playbooks/new', '/admin', 'New playbook', () => [step('Playbooks', '/admin/agent-playbooks')]),
	record('/admin/agent-playbooks/:id', '/admin', 'Playbook', { crumbs: () => [step('Playbooks', '/admin/agent-playbooks')] }),
	// Reached from a Slack card or a notification, never the rail.
	fixed('/agent-approvals/:id', '/notifications', 'Agent approval'),
	// Reached from a repeating task's Slack receipt.
	fixed('/agent-schedules', '/notifications', 'My repeating tasks'),
	// Reached from a mission's Slack status message.
	fixed('/agent-missions', '/notifications', 'Missions'),
	record('/agent-missions/:id', '/notifications', 'Mission', { crumbs: () => [step('Missions', '/agent-missions')] }),
	// Workflow runs (W1): reached from a run's link (an agent's answer, a
	// start); the Workflows menu entry arrives with the canvas (W5).
	fixed('/workflow-inbox', '/notifications', 'Waiting on you'),
	fixed('/workflow-runs', '/notifications', 'Workflow runs'),
	record('/workflow-runs/:id', '/notifications', 'Workflow run', { crumbs: () => [step('Workflow runs', '/workflow-runs')] }),
	fixed('/admin/design-system', '/admin', 'Design system'),
	fixed('/admin/design-settings', '/admin', 'Design settings'),
	fixed('/admin/triage-review', '/admin', 'Triage review'),
];

const BY_PATTERN = new Map(TRAIL_REGISTRY.map((e) => [e.pattern, e]));

const EMPTY_LABELS: ReadonlyMap<string, string> = new Map();

/** The entry for a route pattern, or undefined. */
export function trailEntryFor(pattern: string): TrailEntry | undefined {
	return BY_PATTERN.get(pattern);
}

/** Rail hrefs carry a query in the Partner workspace (`/kb?view=partner`);
 *  the trail only ever cares about the path. */
function hrefPath(href: string): string {
	const q = href.indexOf('?');
	return q === -1 ? href : href.slice(0, q);
}

/** Pull `:param` values (and the trailing `*` tail) out of a concrete path. */
export function paramsFor(pattern: string, path: string): { params: Record<string, string>; tail: string[] } {
	const patternSegments = pattern.split('/').filter(Boolean);
	const pathSegments = path.split('/').filter(Boolean);
	const params: Record<string, string> = {};
	let tail: string[] = [];
	patternSegments.forEach((seg, i) => {
		if (seg === '*') tail = pathSegments.slice(i);
		else if (seg.startsWith(':')) params[seg.slice(1)] = pathSegments[i] ?? '';
	});
	return { params, tail };
}

function resolveLabel(fallback: TrailEntry['leafFallback'], p: TrailParams): string {
	return (typeof fallback === 'function' ? fallback(p) : fallback).trim();
}

function sectionCrumb(href: string, sections: readonly TrailSection[]): Crumb {
	const wanted = hrefPath(href);
	const match =
		sections.find((s) => hrefPath(s.href) === wanted) ??
		CHROME_SECTIONS.find((s) => s.href === wanted);
	// A rail the current user cannot see (a deep link into another
	// workspace) still needs a first crumb rather than a hole, so the last
	// resort is the href's own first segment, humanised.
	return {
		label: match?.label ?? humaniseSegment(wanted.split('/').filter(Boolean)[0] ?? '') ?? '',
		href,
		icon: match?.icon,
	};
}

/** The alternate parent, if the page the user actually arrived from is one
 *  of this entry's declared alternates (spec §5). */
function alternateParentCrumb(
	entry: TrailEntry,
	from: string | null,
	sections: readonly TrailSection[],
): Crumb | null {
	if (!from || !entry.alternateParents?.length) return null;
	const fromPath = hrefPath(from);
	const pattern = entry.alternateParents.find((alt) => routePatternMatches(alt, fromPath));
	if (!pattern) return null;
	const altEntry = BY_PATTERN.get(pattern);
	if (!altEntry) return null;
	const { params, tail } = paramsFor(pattern, fromPath);
	const p: TrailParams = { params, path: fromPath, query: new URLSearchParams(from.slice(from.indexOf('?') + 1)), tail };
	// The alternate's OWN label, from its own registry entry: "My tickets"
	// for /tickets/mine, "Map" for /partners/map. A top-level alternate
	// (/deals/board is not one, /partners/:id is) falls back to its section.
	const label =
		altEntry.leaf === 'none'
			? sectionCrumb(altEntry.section ?? '', sections).label
			: resolveLabel(altEntry.leafFallback, p);
	if (!label) return null;
	return { label, href: from };
}

/**
 * The trail for a page: section crumb, intermediates, current crumb.
 *
 * @param path         `location.pathname`
 * @param search       `location.search` (leading `?` optional)
 * @param leaf         the live `services/trail.ts` leaf, or null
 * @param from         `history.state?.from` — where the user came from
 * @param sections     the rail as it renders right now (the shell's nav items)
 * @param crumbLabels  names published for INTERMEDIATE crumbs by href
 *                     (`services/trail.ts`'s `setCrumbLabel`), so the partner
 *                     between Partners and Implementation reads as a library
 *                     rather than as the id the URL happens to carry
 */
export function resolveTrail(
	path: string,
	search = '',
	leaf: string | null = null,
	from: string | null = null,
	sections: readonly TrailSection[] = [],
	crumbLabels: ReadonlyMap<string, string> = EMPTY_LABELS,
): Crumb[] {
	const pattern = matchingRoutePattern(path);
	if (!pattern) return [];
	const entry = BY_PATTERN.get(pattern);
	if (!entry || entry.section === null) return [];

	const { params, tail } = paramsFor(pattern, path);
	const p: TrailParams = { params, path, query: new URLSearchParams(search), tail };

	const crumbs: Crumb[] = [sectionCrumb(entry.section, sections)];
	const middle = entry.crumbs?.(p) ?? [];
	crumbs.push(...middle);

	// A page names its intermediates by href; the registry only ever knew the
	// id. Never the section crumb (that is the rail's own label) and never the
	// current crumb (that is the leaf's job).
	if (crumbLabels.size && middle.length) {
		for (let i = 1; i < crumbs.length; i++) {
			const named = crumbs[i].href ? crumbLabels.get(hrefPath(crumbs[i].href as string)) : undefined;
			if (named) crumbs[i] = { ...crumbs[i], label: named };
		}
	}

	// The current page's own crumb.
	//
	// A `static` leaf is the ROUTE's own word — "Amend", "Renew", "New
	// contract" — and it must win over anything the page publishes. The renew
	// and amend editors share their component with the record page, which
	// sets the contract number as the leaf, and the trail then read
	// "Contracts > 2018-661" with no sign you were mid-renewal.
	// A `none` entry takes a supplied leaf only if it says it wants one
	// (`acceptsLeaf`, spec §4.8): a rail landing that never takes a leaf
	// cannot be decorated by one that outlived its own page.
	const label =
		entry.leaf === 'static'
			? resolveLabel(entry.leafFallback, p)
			: entry.leaf === 'none'
				? (entry.acceptsLeaf ? (leaf?.trim() ?? '') : '')
				: leaf?.trim() || resolveLabel(entry.leafFallback, p);
	if (label) crumbs.push({ label });

	// Arrival: the page the user actually came from replaces the parent
	// crumb, which is the last crumb before the current one.
	const alternate = alternateParentCrumb(entry, from, sections);
	if (alternate && crumbs.length > 1) crumbs[crumbs.length - 2] = alternate;

	crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], href: undefined, current: true };
	return crumbs;
}

/** True when the shell should render a skeleton in place of the leaf: a
 *  record route whose page has not called `setLeaf` yet (spec §5). */
export function trailIsAwaitingLeaf(path: string, leaf: string | null): boolean {
	const pattern = matchingRoutePattern(path);
	if (!pattern) return false;
	const entry = BY_PATTERN.get(pattern);
	return !!entry && entry.section !== null && entry.leaf === 'record' && !leaf;
}

/** Every pattern the registry knows — the lint test's other half. */
export function trailRegistryPatterns(): string[] {
	return TRAIL_REGISTRY.map((e) => e.pattern);
}

export { ROUTE_PATTERNS };
